import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'

const components = {
  MuiCssBaseline: {
    styleOverrides: `
      html {
        scroll-behavior: smooth;
        -webkit-text-size-adjust: 100%;
      }
      body {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        word-break: keep-all;
        overflow-wrap: anywhere;
        letter-spacing: -0.02em;
      }
      a,
      button {
        color: inherit;
        text-decoration: none;
        -webkit-text-fill-color: currentColor;
      }
      label {
        display: block;
      }
    `,
  },

  MuiTypography: {
    variants: [
      {
        props: { variant: 'body3' },
        style: ({ theme }) => ({
          fontSize: theme.typography.body3.fontSize,
          lineHeight: theme.typography.body3.lineHeight,
        }),
      },
    ],
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.radius('lg'),
        transition: theme.transitions.create(['box-shadow', 'transform'], {
          duration: theme.transitions.duration.shortest,
        }),
      }),
    },
    variants: [
      {
        props: { variant: 'elevation' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          boxShadow: theme.shadows[1],
          border: 'none',
        }),
      },

      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
        }),
      },

      {
        props: { variant: 'grey' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey[50],
          boxShadow: 'none',
        }),
      },

      {
        props: { variant: 'filled' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey[500],
          color: theme.palette.common.white,
          boxShadow: theme.shadows[2],
        }),
      },
      {
        props: { variant: 'transparent' },
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none',
        },
      },
    ],
  },

  MuiLink: {
    defaultProps: {
      color: 'inherit',
      underline: 'hover',
    },
    styleOverrides: {
      root: {
        '&:hover': {
          color: 'inherit',
        },
      }
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: '96px',
        fontWeight: '500',
        textTransform: 'none',
        borderRadius: theme.shape.radius.md,
        boxShadow: 'none',
        transition: theme.transitions.create(['background-color', 'color', 'border-color'], {
          duration: 200,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }),
        '&:hover': { boxShadow: 'none' },
        '&:disabled': {
          color: theme.palette.grey[500],
          backgroundColor: theme.palette.grey[100],
        },
      }),
    },
    variants: [
      {
        props: { variant: 'outlined', color: 'grey' },
        style: ({ theme }) => ({
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.grey[0],
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.grey[800],
            backgroundColor: theme.palette.grey[0],
          },
          '&:active': {
            borderColor: theme.palette.grey[800],
            backgroundColor: theme.palette.grey[50],
          },
          '&:disabled': {
            borderColor: theme.palette.divider,
            color: theme.palette.grey.light,
          },
        }),
      },
      {
        props: { variant: 'contained', color: 'grey' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.grey[0],
          '&:hover': {
            backgroundColor: theme.palette.grey[900],
          },
          '&:active': {
            backgroundColor: theme.palette.grey[1000],
          },
        }),
      },
      {
        props: { variant: 'outlined', color: 'primary' },
        style: ({ theme }) => ({
          border: `1px solid ${theme.palette.primary[500]}`,
          color: theme.palette.primary[500],
          backgroundColor: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.primary[50],
            borderColor: theme.palette.primary[500],
          },
          '&:active': {
            backgroundColor: theme.palette.primary[100],
          },
        }),
      },
      {
        props: { variant: 'contained', color: 'primary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.primary[500],
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: theme.palette.primary[600],
          },
          '&:active': {
            backgroundColor: theme.palette.primary[700],
          },
        }),
      },
      {
        props: { size: 'xsmall' },
        style: { height: '32px', borderRadius: '4px', fontSize: '15px', padding: '0 8px', lineHeight: '1.5' },
      },
      {
        props: { size: 'small' },
        style: { height: '40px', borderRadius: '8px', fontSize: '15px', padding: '0 16px', lineHeight: '1.5' },
      },
      {
        props: { size: 'medium' },
        style: { height: '48px', borderRadius: '8px', fontSize: '17px', padding: '0 16px', lineHeight: '1.5' },
      },
      {
        props: { size: 'large' },
        style: { height: '56px', borderRadius: '8px', fontSize: '17px', padding: '0 16px', lineHeight: '1.5' },
      },
      {
        props: { size: 'table' },
        style: { minWidth: '48px', height: '32px', borderRadius: '4px', fontSize: '15px', padding: '0 8px', lineHeight: '1.5' },
      },
    ],
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        borderRadius: theme.shape.radius('md'),
        backgroundColor: theme.palette.common.white,
        overflow: 'hidden',
        padding: 0,
        borderColor: theme.palette.divider,
        transition: theme.transitions.create(['border-color', 'background-color'], {
          duration: theme.transitions.duration.shorter,
        }),

        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
          },

        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.grey[400],
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.grey[900],
          borderWidth: '1px',
        },

        '& .MuiSelect-select[aria-expanded="true"] ~ fieldset': {
          borderColor: theme.palette.primary.main,
        },

        '& .MuiSelect-select[aria-expanded="true"]': {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },

        '&:has(.MuiSelect-select[aria-expanded="true"])': {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },

        '&.transparent': {
          backgroundColor: 'transparent !important',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '& .MuiSelect-select': {
            backgroundColor: 'transparent !important',
            paddingLeft: 0,
          },
          '& svg': {
            right: 0,
          },
          // "&.Mui-focusVisible": {
          //   border: "2px solid red"
          // },
          // "& :focus-visible + *": {
          //   border: "2px solid blue"
          // },
        },

        // disabled
        '&.Mui-disabled': {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[500],
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider, 
          },
        },

        // readonly
        '&.Mui-readOnly': {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.primary,
          cursor: 'default',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider, 
          },
        },

        // error
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.error.main,
        },

        '& .MuiInputBase-input': {
          height: '48px',
          boxSizing: 'border-box',
          padding: theme.spacing(0, 2),
          color: theme.palette.text.primary,
          '&::placeholder': {
            color: theme.palette.grey.light,
            opacity: 1,
          },
        },

        '& .MuiInputBase-inputMultiline': {
          resize: 'none',
          backgroundColor: theme.palette.common.white,
          padding: theme.spacing(1.5, 2),
          boxSizing: 'border-box',
        },
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      displayEmpty: true,
      IconComponent: KeyboardArrowDownRoundedIcon,
    },
    styleOverrides: {
      select: ({ theme, ownerState }) => ({
        height: '48px',
        display: 'block',
        alignItems: 'center',
        paddingRight: `${theme.spacing(5)} !important`,
        backgroundColor: theme.palette.common.white,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: '48px',
        minWidth: 0,

        '&:focus-visible': {
          outline: 'auto',
          outlineOffset: 2,
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[500],
        },
        '&.Mui-readOnly': {
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.text.primary,
        },
        '& option': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '&.transparent': {
          backgroundColor: 'transparent !important',
          borderColor: 'transparent !important',
        },
      }),

      icon: ({ theme }) => ({
        right: theme.spacing(2),
        color: theme.palette.grey[800],
        primaryerEvents: 'none',
        transition: theme.transitions.create(['transform'], {
          duration: theme.transitions.duration.shorter,
        }),
        '.Mui-focused &': {
          color: theme.palette.grey[900],
        },
        '.Mui-disabled &': {
          color: theme.palette.grey.dark,
        },
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body3,
        height: '28px',
        minWidth: '60px',
        borderRadius: '30px',
        fontWeight: '600',
        padding: theme.spacing(0, 1),
        borderWidth: '1px',
        borderStyle: 'solid',
        backgroundColor: 'transparent',
        transition: theme.transitions.create(
          ['background-color', 'color', 'border-color'],
          { duration: theme.transitions.duration.shortest }
        ),
      }),
      label: {
        padding: 0,
      },
    },

    variants: [
      {
        props: { variant: 'outlined' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.common.white,
        }),
      },
      {
        props: { variant: 'outlined', color: 'primary' },
        style: ({ theme }) => ({
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        }),
      },
      {
        props: { variant: 'contained', color: 'primary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          borderColor: theme.palette.primary.main,
        }),
      },
      {
        props: { variant: 'outlined', color: 'secondary' },
        style: ({ theme }) => ({
          borderColor: theme.palette.secondary.main,
          color: theme.palette.secondary.main,
        }),
      },
      {
        props: { variant: 'contained', color: 'secondary' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.common.white,
          borderColor: theme.palette.secondary.main,
        }),
      },
      {
        props: { variant: 'outlined', color: 'secondary2' },
        style: ({ theme }) => ({
          borderColor: theme.palette.secondary2.main,
          color: theme.palette.secondary2.main,
        }),
      },
      {
        props: { variant: 'contained', color: 'secondary2' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.secondary2.main,
          color: theme.palette.common.white,
          borderColor: theme.palette.secondary2.main,
        }),
      },
      {
        props: { variant: 'outlined', color: 'grey' },
        style: ({ theme }) => ({
          borderColor: theme.palette.grey[800],
          color: theme.palette.grey[800],
        }),
      },
      {
        props: { variant: 'contained', color: 'grey' },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.common.white,
          borderColor: theme.palette.grey[800],
        }),
      },
    ],
  },

  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        boxShadow: `none`,
        border: `1px solid ${theme.palette.divider}`,
        borderTopWidth: 0,
        borderRadius: theme.shape.radius('md'),
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        overflow: 'hidden',
        marginTop: 0,
        maxHeight: '240px',
        overflowY: 'auto',
      }),
      list: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        display: 'block',
        height: '48px',
        lineHeight: '48px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: theme.spacing(0, 2),
        color: theme.palette.text.secondary,
        boxShadow: 'none',
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: theme.transitions.duration.shortest,
        }),

        '&:not(.Mui-selected):not(:hover)': {
          backgroundColor: theme.palette.common.white,
        },

        '&:hover': {
          backgroundColor: theme.palette.primary[50],
        },

        '&.Mui-selected': {
          backgroundColor: `${theme.palette.primary[50]} !important`,
          color: theme.palette.text.primary, 
          '&:hover': {
            backgroundColor: theme.palette.primary[50],
          },
        },

        // disabled
        '&.Mui-disabled': {
          color: theme.palette.grey[400],
          backgroundColor: theme.palette.common.white,
        },
      }),
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.body3.fontSize,
        color: theme.palette.text.secondary,
        padding: theme.spacing(0, 1),
        marginLeft: 0,
      }),
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: '60px', 
        padding: '12px', 
        BorderBottom: '1px solid', 
        borderColor: theme.palette.divider,
      })
    }
  },

  MuiTableRow: {
    styleOverrides: {
      root: { '&:hover': { backgroundColor: 'transparent !important' } },
    }
  },

  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.divider,
        transition: theme.transitions.create(['color', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&.Mui-disabled': {
          color: theme.palette.grey[300],
          opacity: 0.6,
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.grey[900]}`,
          outlineOffset: -9,
        },
      }),
    },

    defaultProps: {
      disableRipple: true, 
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.divider,
        transition: theme.transitions.create(['color', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),

        '&.Mui-checked': {
          color: theme.palette.primary.main,
        },

        '&.Mui-disabled': {
          color: theme.palette.grey[300],
          opacity: 0.6,
        },

        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.grey[900]}`,
          outlineOffset: -7,
        },
      }),
    },

    defaultProps: {
      disableRipple: true,
    },
  },

  MuiPagination: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginTop: theme.spacing(3),
        '& .MuiPaginationItem-root': {
          ...theme.typography.body2,
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest,
          }),

          '&.Mui-selected': {
            backgroundColor: theme.palette.grey[800],
            color: theme.palette.common.white,
          },

          '&:not(.Mui-selected):hover': {
            backgroundColor: theme.palette.grey[50],
          },
        },
      }),
    },
  },

  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        margin: 0,
        whiteSpace: 'nowrap',

        '&.Mui-selected': {
          backgroundColor: theme.palette.grey[800],
          color: theme.palette.common.white,
        },

        '&:not(.Mui-selected):hover': {
          backgroundColor: theme.palette.grey[50],
        },

        '&.Mui-disabled': {
          opacity: 0.4,
        },
      }),
      icon: ({ theme }) => ({
        fontSize: '20px',
        color: theme.palette.grey[800],
      }),
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.body2,
        color: theme.palette.text.secondary,
        alignItems: 'flex-start',
      }),
      icon: ({ theme }) => ({
        fontSize: '20px',
        marginRight: '4px',
        paddingTop: '3px !important',
      }),
      message: ({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        marginTop: '2px',
        color: theme.palette.text.secondary,
      }),
    },

    variants: [
      {
        props: { severity: 'info' },
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.grey.light,
          padding: 0,
          '& .MuiAlert-icon': {
            color: theme.palette.grey.light,
          },
        }),
      },
    ],
  },
}

export default components